'use strict';

import * as THREE from 'three';

import { SkyViewContextMenuProps } from '../../react/components/skyview/context-menu';
import { SkyviewAssetPositionProps } from '../../react/components/skyview/tooltip/skyview-asset-position-tooltip';
import { MetaDefinition } from '../../services/metadata/meta-types';
import { FeatureFlagsManager } from '../../utils/core/feature-flags-manager';
import { isDefined } from '../../utils/variables';
import { AssetManager } from '../asset-manager';
import cameraManager from '../camera-manager';
import { SkyviewCamera } from '../cameras/types';
import { CoordinateManager } from '../coordinate-manager';
import { Conic } from '../elements/design/design-node/utils/conic';
import { ElementObj } from '../elements/element-obj';
import { Geometry } from '../elements/geometry';
import { InputHandler } from '../input-handler';
import { publish, SubscriptionTopic } from '../messaging/pubsub';
import {
  KeyInputData,
  MouseButtonType,
  MouseInputData,
  TouchInputData,
} from '../utilities/input-event/input-event-data-extractor';
import { clamp, Coordinates } from '../utilities/math-helpers';
import { Pick, setRayCasterParamsBasedOnCamera } from '../utilities/pick';
import { PickingSphere } from './picking-sphere';
import { Tool } from './tool';
import { buildTooltipProps, MenuTooltipTool, showToolTooltip } from './tooltip-helper';
import { AssetSnapping } from './utils/asset-snapping';
import { copyCoordinate } from './utils/context-menu/commands';
import { HeightLayersGuide } from './utils/height-layers';
import { CenterCurveHighlight } from './utils/snap-candidates';

enum MeasureToolState {
  Measuring,
  Area,
  Enabled,
  Disabled,
}

enum CursorColor {
  PlacePoint = 0xeeee88,
  ConnectArea = 0x88ee88,
}

export class MeasureTool extends Tool {
  assetMeasuring = false;
  /**
   * The snapping set in the snapping dialog, which replaces assetMeasuring with the flag on.
   */
  snapping = new AssetSnapping({});
  private centerCurves?: Conic[];
  private centerHighlight = new CenterCurveHighlight();
  /**
   * All heights at the snapped point, while its option is on.
   */
  readonly heightsGuide = new HeightLayersGuide();
  private lastMousePosition = new THREE.Vector2();

  tooltipCallback?: (tooltip?: SkyviewAssetPositionProps) => void = undefined;
  /**
   * Set while the tooltip is shown in the snapping dialog, which then gets it instead of the cursor.
   */
  onMenuTooltipChanged?: MenuTooltipTool['onMenuTooltipChanged'];
  contextMenuCallback?: (contextMenu?: SkyViewContextMenuProps) => void = undefined;

  currentPoint: THREE.Vector3 | undefined;
  private currentObj: Geometry;
  private state: MeasureToolState;

  private coordinates: Coordinates = {
    easting: 0,
    northing: 0,
    height: 0,
  };

  private meta: MetaDefinition;
  private cursor: PickingSphere;

  constructor(inputHandler: InputHandler) {
    super('measure', inputHandler);

    this.visible = false;
    this.currentObj = new Geometry();
    this.cursor = new PickingSphere();
    this.cursor.visible = true;
    this.add(this.cursor);
    this.add(this.centerHighlight);
    this.add(this.heightsGuide);

    this.state = MeasureToolState.Disabled;

    this.meta = { src: '', parameters: [] };
    this.setCursor(0xffff00);
  }

  onTooltipChanged(callback: (tooltip?: SkyviewAssetPositionProps) => void) {
    this.tooltipCallback = callback;
  }

  onContextMenuChanged(callback: (contextMenu?: SkyViewContextMenuProps) => void) {
    this.contextMenuCallback = callback;
  }

  setCursor(hexColor: number) {
    if (!this.cursor) {
      this.cursor = new PickingSphere(new THREE.Color(hexColor));
    } else {
      this.cursor.setColor(new THREE.Color(hexColor));
    }
  }

  getMeta(sweref?: string): MetaDefinition {
    return this.meta;
  }

  // OBS WARN Touch är INTE implementerat med all funktionalitet.
  onTouchStart(e: TouchInputData): boolean {
    return this.onTouchMove(e);
  }

  onTouchMove(e: TouchInputData) {
    return true;
  }

  refreshCursor(point: THREE.Vector3) {
    this.position.copy(point);
    this.coordinates = CoordinateManager.getInstance().localToProjectCoords(point);
    publish(SubscriptionTopic.ViewerCursorPositionChanged, this.coordinates);
  }

  get snappingOptions() {
    return FeatureFlagsManager.instance.isEnabled('SK-7558-measure-snapping-options');
  }

  /**
   * Shows where the cursor snaps to: the cursor is hidden where nothing is snapped to, and the
   * curves whose center it snaps to are highlighted.
   */
  private showSnapped(point: THREE.Vector3 | undefined) {
    const camera = cameraManager().getActiveCamera();

    if (camera) {
      this.heightsGuide.update(camera, point);
    }

    this.cursor.visible = isDefined(point);
    // In world coordinates, although the tool follows the cursor.
    this.centerHighlight.position.copy(this.position).negate();
    this.centerHighlight.show(point && this.centerCurves);
  }

  pick(camera: SkyviewCamera, e: MouseInputData) {
    const pixelSnapThreshold = 20;
    const minPickingDistance = 0.1;
    const maxPickingDistance = 350;

    const rayCaster = camera.getRay(e.pointer.position.x, e.pointer.position.y);
    setRayCasterParamsBasedOnCamera(
      camera.getTHREECamera(),
      rayCaster,
      pixelSnapThreshold,
      clamp(
        this.cursor.position.distanceTo(rayCaster.ray.origin),
        minPickingDistance,
        maxPickingDistance,
      ),
    );

    if (this.snappingOptions) {
      const { point, curves } = this.snapping.findSnapPoint(camera, rayCaster, {
        fallbackHeight: 0,
      });
      this.centerCurves = curves;
      return point;
    }

    return Pick.instance.closestPoint(camera, rayCaster, {
      skipAssets: !this.assetMeasuring,
      pickHeight: 0,
    });
  }

  onMouseMove(e: MouseInputData) {
    const camera = cameraManager().getActiveCamera();

    if (!camera) {
      return true;
    }

    let tmpPoint = this.pick(camera, e);

    if (!tmpPoint) {
      // Nothing to snap to, e.g. away from the points of the snap styles: nothing is placed.
      this.currentPoint = undefined;
      this.showSnapped(undefined);
      this.clearTooltip();
      return true;
    }

    const curPoints = this.currentObj.getWorldPoints();

    // See if we are trying to connect an area.
    if (curPoints.length > 3) {
      const distance = this.currentObj.screenDistanceToPoint(
        0,
        e.pointer.client.x,
        e.pointer.client.y,
        camera,
      );
      if (distance <= 10) {
        this.setCursor(CursorColor.ConnectArea);
        tmpPoint = curPoints[0];
        this.currentObj.setArea(true);
        this.state = MeasureToolState.Area;
      } else {
        this.setCursor(CursorColor.PlacePoint);
        this.currentObj.setArea(false);
        this.state = MeasureToolState.Measuring;
      }
    }

    if (curPoints.length > 0) {
      const len = curPoints.length;
      this.currentObj.recalculate(len - 1, tmpPoint);
    }
    this.refreshCursor(tmpPoint);
    this.showSnapped(tmpPoint);

    this.lastMousePosition = e.viewportPos
      ? new THREE.Vector2(e.viewportPos.clientX, e.viewportPos.clientY)
      : e.pointer.client;

    // eslint-disable-next-line no-prototype-builtins
    if (!e.hasOwnProperty('isNativeFunction')) {
      this.updateTooltip(tmpPoint);
      this.contextMenuCallback?.();
    }

    this.currentPoint = tmpPoint;

    return true;
  }

  onMouseOut(e: MouseInputData) {
    this.clearTooltip();
    return true;
  }

  onMouseDown(e: MouseInputData) {
    this.mousePosition = e.pointer.client;
    return true;
  }

  onKeyDown(input: KeyInputData) {
    this.inputHandler.handleKeyPress(
      input,
      'c',
      () => {
        void copyCoordinate({
          x: this.coordinates.easting,
          y: this.coordinates.northing,
          z: this.coordinates.height,
        }).action();
      },
      'Control',
    );
    this.inputHandler.handleKeyPress(input, 'Enter', () => {
      this.saveAsset();
    });

    if (this.currentObj.getWorldPoints().length > 0) {
      this.inputHandler.handleKeyPress(input, 'Escape', () => {
        this.inputHandler.setTool('measure');
      });
    }
  }

  private saveAsset(e?: MouseInputData) {
    const pointCount = this.currentObj.getLocalPoints().length;

    if (pointCount > 1) {
      if (this.state === MeasureToolState.Area) {
        this.currentObj.removePoint(this.currentObj.getLocalPoints().length - 1);
      }

      publish(SubscriptionTopic.ShowSaveGeometryAssetDialog, {
        asset: this.currentObj,
        client: this.lastMousePosition,
      });

      // eslint-disable-next-line no-prototype-builtins
      if (e?.hasOwnProperty('isNativeFunction')) {
        this.disable();
      } else {
        this.inputHandler.setTool('measure');
      }
    }
  }

  async onMouseUp(e: MouseInputData) {
    if (!this.withinThreshold(e.pointer.client.x, e.pointer.client.y)) {
      return true;
    }

    if (e.button.type === MouseButtonType.RightButton || this.state === MeasureToolState.Area) {
      // Disable the tool if right clicking with zero points placed.
      if (this.currentObj.getLocalPoints().length === 0) {
        this.inputHandler.setTool('select');
        return true;
      }

      this.saveAsset(e);
      return true;
    }

    if (e.button.type === MouseButtonType.LeftButton && this.currentPoint) {
      if (this.withinThreshold(e.pointer.client.x, e.pointer.client.y)) {
        // Deselect other assets when creating.
        AssetManager.instance.select(undefined);
        this.currentObj.addPoint(this.currentPoint, false);
        if (this.currentObj.getLocalPoints().length === 1) {
          this.currentObj.addPoint(
            new THREE.Vector3(this.currentPoint.x + 0.05, this.currentPoint.y, this.currentPoint.z),
            false,
          );
        }

        this.refreshCursor(this.currentPoint);
        this.updateTooltip(this.currentPoint);
        this.currentPoint = undefined;
      }
    }

    return Promise.resolve(true);
  }

  onMouseWheel(e: MouseInputData) {
    const camera = cameraManager().getActiveCamera();

    if (!camera) {
      return true;
    }

    const tmpPoint = this.pick(camera, e);

    if (!tmpPoint) {
      return true;
    }

    this.refreshCursor(tmpPoint);
    this.showSnapped(tmpPoint);

    return true;
  }

  onProjectProjectionUpdated() {
    if (this.currentPoint) {
      this.currentPoint.add(CoordinateManager.getInstance().origoDifference);
    }
    return true;
  }

  enable() {
    this.visible = true;
    this.cursor.visible = true;
    this.inputHandler.setCursor('crosshair');

    this.currentObj = new Geometry();
    this.setCursor(0xeeee88);

    AssetManager.instance.addTemp(this.currentObj);

    this.currentObj.setDepthTest(false);

    this.position.set(0, 0, 0);
    this.state = MeasureToolState.Enabled;
  }

  disable() {
    this.assetMeasuring = false;
    this.centerCurves = undefined;
    this.showSnapped(undefined);
    this.visible = false;
    this.inputHandler.setCursor('');
    publish(SubscriptionTopic.ViewerCursorPositionChanged, null);

    this.clearTooltip();
    this.clearContextMenu();

    AssetManager.instance.removeTemp(this.currentObj);
    this.currentObj = new Geometry();

    this.state = MeasureToolState.Disabled;

    this.meta = { src: '', parameters: [] };
  }

  isEditing() {
    return this.state === MeasureToolState.Measuring;
  }

  updateTooltip(point: THREE.Vector3) {
    if (!this.currentPoint) {
      return;
    }

    const camera = cameraManager().getActiveCamera();
    if (!camera) {
      return;
    }

    const screenPoint = camera.getScreenPosition(point);

    showToolTooltip(this, {
      ...buildTooltipProps(this.currentObj as ElementObj, camera, this.coordinates, {
        x: screenPoint.x,
        y: screenPoint.y,
      }),
      heights: this.heightsGuide.getTooltipHeights(point),
    });
  }

  clearTooltip() {
    publish(SubscriptionTopic.ViewerCursorPositionChanged, null);
    showToolTooltip(this);
  }
  clearContextMenu() {
    this.contextMenuCallback?.();
  }
}
